import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('reparation');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  return (
    <section
      ref={ref}
      className="relative min-h-[480px] md:min-h-[520px] flex items-center pt-16"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20roofing%20crew%20working%20on%20steep%20residential%20roof%20in%20Quebec%20winter%2C%20installing%20blue%20waterproof%20emergency%20tarp%20over%20damaged%20shingles%2C%20dramatic%20cloudy%20sky%2C%20warm%20afternoon%20light%2C%20documentary%20photography%20style%2C%20realistic%20construction%20site%2C%20Canada%2C%204K&width=1920&height=700&seq=rep1&orientation=landscape"
          alt="Couvreur en intervention urgence toiture à Lévis — bâchage temporaire hiver par couvreur RBQ"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-primary/90 via-slate-primary/75 to-slate-primary/50" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6 lg:px-10 w-full py-12 md:py-16">
        {/* Breadcrumb */}
        <nav
          className={`mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <ol className="flex items-center gap-2 text-sm text-white/70">
            <li>
              <button
                onClick={() => {
                  if (window.REACT_APP_NAVIGATE) window.REACT_APP_NAVIGATE('/');
                }}
                className="hover:text-copper transition-colors"
              >
                {t('breadcrumb_home')}
              </button>
            </li>
            <li><i className="ri-arrow-right-s-line text-xs" /></li>
            <li className="text-white/90">{t('breadcrumb_current')}</li>
          </ol>
        </nav>

        {/* H1 */}
        <h1
          className={`font-manrope font-bold text-white text-[40px] md:text-4xl lg:text-[56px] lg:leading-tight max-w-3xl mb-4 md:mb-6 transition-all duration-1000 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {t('hero_h1')}
        </h1>

        <p
          className={`text-white/80 text-base md:text-lg lg:text-xl max-w-2xl mb-8 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {t('hero_subtitle')}
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-col sm:flex-row gap-4 mb-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <a
            href="tel:+14184769950"
            aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
            title="Cliquez pour appeler"
            className="inline-flex items-center justify-center gap-2 bg-urgent hover:bg-urgent-dark text-white font-bold px-7 py-3.5 rounded-md text-base transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            {t('hero_cta_call')}
          </a>
          <button
            onClick={() => {
              const el = document.getElementById('quote-form');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center justify-center gap-2 bg-copper hover:bg-copper-dark text-white font-bold px-7 py-3.5 rounded-md text-base transition-all duration-300 whitespace-nowrap"
          >
            {t('hero_cta_inspection')}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {/* Badges */}
        <div
          className={`flex flex-wrap gap-3 md:gap-5 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {[
            { icon: 'ri-time-line', text: t('hero_badge_1') },
            { icon: 'ri-calendar-check-line', text: t('hero_badge_2') },
            { icon: 'ri-shield-check-line', text: t('hero_badge_3') },
          ].map((badge, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 text-white/80 text-sm bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full"
            >
              <i className={`${badge.icon} text-copper`} />
              {badge.text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}